import { useEffect, useState } from "react";
import { TESTIDS } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import EvidenceTable from "@/components/EvidenceTable";
import { FileSearch } from "lucide-react";

function bandColorClass(score) {
  if (score >= 80) return "score-strong";
  if (score >= 60) return "score-mid";
  if (score >= 40) return "score-mid";
  return "score-low";
}

function glowColor(score) {
  if (score >= 60) return "rgba(107,122,85,0.28)";
  if (score >= 40) return "rgba(166,124,63,0.24)";
  return "rgba(198,93,58,0.22)";
}

export default function ScoreCard({ tier1, evidence }) {
  const [display, setDisplay] = useState(0);
  const target = tier1.knownfor_score ?? 0;

  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1800;
    const step = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  const bandCls = bandColorClass(target);

  return (
    <div
      className="glass rounded-3xl p-8 md:p-14 relative overflow-hidden"
      style={{ "--glow-color": glowColor(target) }}
    >
      <div className="score-glow" />
      <div className="relative grid md:grid-cols-5 gap-8 items-center">
        <div className="md:col-span-2 text-center md:text-left">
          <div className="label-eyebrow mb-3">
            FieldGuide Local Authority Score
            <sup style={{ fontSize: "0.6em", marginLeft: "1px", letterSpacing: "0" }}>™</sup>
          </div>
          <div
            className={`font-serif font-bold leading-none ${bandCls}`}
            style={{ fontSize: "clamp(6rem, 15vw, 12rem)", paddingBottom: "0.15em" }}
            data-testid={TESTIDS.reportScoreValue}
          >
            {display}
          </div>
          <div className="font-mono text-sm text-[color:var(--text-muted)] mt-4">out of 100</div>
        </div>

        <div className="md:col-span-3">
          <div className="label-eyebrow mb-2">Score band</div>
          <div
            className={`font-serif text-3xl md:text-4xl leading-tight mb-6 ${bandCls}`}
            data-testid={TESTIDS.reportScoreBand}
          >
            {tier1.score_band}
          </div>

          {/* Peer benchmark */}
          <div className="glass rounded-xl p-4 md:p-5" data-testid={TESTIDS.reportPeerBenchmark}>
            <div className="label-eyebrow mb-1.5">Peer benchmark</div>
            {tier1.peer_benchmark_available ? (
              <div>
                <div className="font-mono text-sm">
                  <span style={{ color: "var(--text-primary)" }}>{tier1.knownfor_score}</span>
                  <span className="text-[color:var(--text-muted)]">
                    {" "}
                    vs. median of {tier1.peer_median} across {tier1.peer_count} comparable properties in your market
                  </span>
                </div>
                <div
                  className={`mt-1 text-sm font-medium ${
                    (tier1.peer_delta ?? 0) >= 0 ? "score-strong" : "score-low"
                  }`}
                >
                  {(tier1.peer_delta ?? 0) >= 0 ? "+" : ""}
                  {tier1.peer_delta} points {tier1.peer_delta >= 0 ? "above" : "below"} peer median
                </div>
              </div>
            ) : (
              <div className="text-sm text-[color:var(--text-secondary)]">
                Building peer baseline for this market. Benchmarks unlock once{" "}
                <span className="font-medium" style={{ color: "var(--text-primary)" }}>12+</span> comparable properties in{" "}
                <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>
                  {tier1.property.city}
                </span>{" "}
                have been graded.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Evidence link — lower right */}
      {evidence?.queries?.length ? (
        <div className="relative mt-6 flex justify-end">
          <Dialog>
            <DialogTrigger asChild>
              <button
                className="inline-flex items-center gap-2 text-sm underline underline-offset-4 transition-colors"
                style={{ color: "var(--brand-sage-deep)" }}
                data-testid="scorecard-evidence-trigger"
              >
                <FileSearch className="w-4 h-4" />
                See all {evidence.queries.length} queries with per-platform results
              </button>
            </DialogTrigger>
            <DialogContent
              className="max-w-6xl max-h-[85vh] overflow-y-auto"
              style={{ background: "var(--bg-card)", borderColor: "var(--border-default)" }}
              data-testid="scorecard-evidence-modal"
            >
              <DialogHeader>
                <div className="label-eyebrow mb-1">Evidence</div>
                <DialogTitle className="font-serif text-2xl md:text-3xl">
                  All {evidence.queries.length} queries · per-platform results
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <EvidenceTable queries={evidence.queries} platformResults={evidence.platformResults} />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : null}
    </div>
  );
}
